/**
 * IndexedDB cache so the app opens instantly and reads work offline.
 * Entries are keyed by seq; everything else lives in a small kv store.
 */
import type { Account, Entry, Item, Party } from '@khata/core';

const NAME = 'khata';
const VERSION = 1;

export interface CachedMaster {
  readonly items: Item[];
  readonly parties: Party[];
  readonly accounts: Account[];
  readonly settings: { shopName: string };
  /** Server khata identity; when it changes the cached entries belong to another khata. */
  readonly khataId?: string;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(NAME, VERSION);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('entries'))
        db.createObjectStore('entries', { keyPath: 'seq' });
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function result<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadCache(): Promise<{
  entries: Entry[];
  master: CachedMaster | null;
} | null> {
  const db = await open();
  if (!db) return null;
  try {
    const tx = db.transaction(['entries', 'kv'], 'readonly');
    const entries = await result(tx.objectStore('entries').getAll() as IDBRequest<Entry[]>);
    const master =
      (await result(tx.objectStore('kv').get('master') as IDBRequest<CachedMaster | undefined>)) ??
      null;
    return { entries, master };
  } catch {
    return null;
  }
}

export async function saveEntries(entries: ReadonlyArray<Entry>): Promise<void> {
  if (entries.length === 0) return;
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction('entries', 'readwrite');
    const store = tx.objectStore('entries');
    for (const e of entries) store.put(e);
    await done(tx);
  } catch {
    // cache only
  }
}

export async function saveMaster(master: CachedMaster): Promise<void> {
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction('kv', 'readwrite');
    tx.objectStore('kv').put(master, 'master');
    await done(tx);
  } catch {
    // cache only
  }
}

export async function clearCache(): Promise<void> {
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction(['entries', 'kv'], 'readwrite');
    tx.objectStore('entries').clear();
    tx.objectStore('kv').clear();
    await done(tx);
  } catch {
    // cache only
  }
}
